/* eslint-disable @typescript-eslint/no-explicit-any */

import { v4 as uuidv4 } from "uuid";

/* -------------------- constants (kept close to helpers) -------------------- */

export const LIST_MODE_KEY = "listMode"; // "new" | "edit"
export const LIST_DRAFT_ID_KEY = "listDraftId";
export const NEW_LIST_RECORD_ID_KEY = "newListRecordId";

export const ORIGIN_STEP_KEY = "listOriginStep";
export const ORIGIN_NODE_KEY = "listOriginNode";

export const evidenceKeys = {
    camera: "linkedDocumentID",
    vehicle: "dynamicViewVehicleId",
    voice: "recordingId",
} as const;

/* -------------------- stable hashing for "unchanged rehydrated" -------------------- */

export function stableStringify(value: any): string {
    const seen = new WeakSet();

    const normalize = (v: any): any => {
        if (v == null) return v;
        if (v instanceof Date) return isNaN(v.getTime()) ? null : v.toISOString();
        if (Array.isArray(v)) return v.map(normalize);

        if (typeof v === "object") {
            if (seen.has(v)) return "[Circular]";
            seen.add(v);

            const out: Record<string, any> = {};
            const keys = Object.keys(v).sort();
            for (const k of keys) out[k] = normalize(v[k]);
            return out;
        }

        return v;
    };

    return JSON.stringify(normalize(value));
}

export function hashString(s: string): string {
    let h = 2166136261;
    for (let i = 0; i < s.length; i++) {
        h ^= s.charCodeAt(i);
        h = Math.imul(h, 16777619);
    }
    return (h >>> 0).toString(16);
}

export function stripTransientKeys(values: Record<string, any>) {
    const v = { ...(values ?? {}) };
    delete (v as any).Camera;
    delete (v as any).Vehicle;
    delete (v as any).Voice;
    delete (v as any)["Voice Recorder"];
    return v;
}

export function stripInternalHydrationKeys(values: Record<string, any>) {
    const v = { ...(values ?? {}) };
    for (const k of Object.keys(v)) {
        if (k.endsWith("__latitude") || k.endsWith("__longitude")) {
            delete (v as any)[k];
        }
    }
    return v;
}

/* -------------------- list mode helpers -------------------- */

export function getListMode() {
    return sessionStorage.getItem(LIST_MODE_KEY); // "new" | "edit" | null
}

export function ensureListDraftId(): string {
    let d = sessionStorage.getItem(LIST_DRAFT_ID_KEY);
    if (!d) {
        d = uuidv4();
        sessionStorage.setItem(LIST_DRAFT_ID_KEY, d);
    }
    return d;
}

export function ensureNewListRecordId(): string {
    let id = sessionStorage.getItem(NEW_LIST_RECORD_ID_KEY);
    if (!id) {
        id = uuidv4();
        sessionStorage.setItem(NEW_LIST_RECORD_ID_KEY, id);
    }
    return id;
}

export function getEditListId(): string | null {
    try {
        const j = JSON.parse(localStorage.getItem("ListId") || "null");
        return typeof j === "string" ? j : j == null ? null : String(j);
    } catch {
        const raw = localStorage.getItem("ListId");
        return raw ? String(raw) : null;
    }
}

export function getAccidentIdForDraft(): string | null {
    try {
        const j = JSON.parse(localStorage.getItem("accidentId") || "null");
        return typeof j === "string" ? j : j == null ? null : String(j);
    } catch {
        const raw = localStorage.getItem("accidentId");
        return raw ? String(raw) : null;
    }
}

export function buildDraftKey(args: {
    tenant?: string | null;
    accidentId?: string | null;
    step: string;
    node: string;
    cameFromList: boolean;
    listMode: string | null;
    listDraftId: string | null;
    editListId: string | null;
}) {
    const t = args.tenant ?? "host";
    const a = args.accidentId ?? "no-accident";

    if (!args.cameFromList) {
        return `draft:${t}:${a}:step:${args.step}:node:${args.node}:normal`;
    }

    const draftIdentity =
        args.listMode === "new"
            ? `new:${args.listDraftId ?? "missing"}`
            : `edit:${args.editListId ?? "none"}`;

    return `draft:${t}:${a}:step:${args.step}:node:${args.node}:${draftIdentity}`;
}

/* -------------------- origin tracking for list navigation -------------------- */

export function setListOrigin(step: string, node: string) {
    sessionStorage.setItem(ORIGIN_STEP_KEY, step);
    sessionStorage.setItem(ORIGIN_NODE_KEY, node);
}

export function getListOrigin(stepFallback: string, nodeFallback: string) {
    const s = sessionStorage.getItem(ORIGIN_STEP_KEY) ?? stepFallback;
    const n = sessionStorage.getItem(ORIGIN_NODE_KEY) ?? nodeFallback;
    return { step: s, node: n };
}

export function clearListOrigin() {
    sessionStorage.removeItem(ORIGIN_STEP_KEY);
    sessionStorage.removeItem(ORIGIN_NODE_KEY);
    sessionStorage.removeItem(LIST_MODE_KEY);
    sessionStorage.removeItem(LIST_DRAFT_ID_KEY);
    sessionStorage.removeItem(NEW_LIST_RECORD_ID_KEY);
}

/* -------------------- evidence helpers -------------------- */

export function readLSString(k: string): string | null {
    const raw = localStorage.getItem(k);
    if (!raw) return null;
    try {
        const j = JSON.parse(raw);
        return typeof j === "string" ? j : String(j ?? "");
    } catch {
        return raw;
    }
}

export function isNonEmptyId(v: string | null | undefined) {
    return !!v && v !== "null" && v !== "undefined" && v.trim().length > 0;
}

export function hasCameraEvidence() {
    return isNonEmptyId(readLSString(evidenceKeys.camera));
}

export function hasVoiceEvidence() {
    return isNonEmptyId(readLSString(evidenceKeys.voice));
}

export function hasVehicleEvidence(formData?: any) {
    return (
        isNonEmptyId(readLSString(evidenceKeys.vehicle)) ||
        isNonEmptyId(formData?.vehicleId)
    );
}

export function hasVehicleData(formData?: any) {
    const vid = formData?.vehicleId;
    const doc = formData?.licenseDiskDoc;
    return (
        (typeof vid === "string" &&
            vid !== "00000000-0000-0000-0000-000000000000" &&
            vid.trim().length > 0) ||
        (typeof doc === "string" && doc.trim().length > 0)
    );
}

export function isVehicleComplete(formData?: any) {
    return hasVehicleEvidence(formData) || hasVehicleData(formData);
}

/* -------------------- cache clearing -------------------- */

export function clearFormCache(opts?: { preserveEvidence?: boolean }) {
    const preserveEvidence = !!opts?.preserveEvidence;

    if (!preserveEvidence) {
        localStorage.removeItem("linkedDocumentID");
        localStorage.removeItem("recordingId");
        localStorage.removeItem("currentRecording");
        localStorage.removeItem("dynamicViewVehicleId");
        localStorage.removeItem("viewVehicleId");
    }

    localStorage.removeItem("documentCount");
    localStorage.removeItem("voiceCount");
}

/* -------------------- hydration builders -------------------- */

export function buildInitialValuesFromAnswerMap(
    schema: Record<string, any>,
    answerMap: Record<string, any> | null,
) {
    const iv: Record<string, any> = {};
    const isField = (v: any) => v && typeof v === "object" && !Array.isArray(v);

    const toYmd = (val: Date) => {
        const y = val.getFullYear();
        const m = String(val.getMonth() + 1).padStart(2, "0");
        const d = String(val.getDate()).padStart(2, "0");
        return `${y}-${m}-${d}`;
    };

    const parseDateToYmd = (val: any): string => {
        if (!val) return "";

        if (typeof val === "string") {
            const s = val.trim();
            if (!s) return "";

            // Native storage format.
            if (/^\d{4}-\d{2}-\d{2}$/.test(s)) return s;

            // Legacy UI format: dd/MM/yyyy (or d/M/yyyy) and dash variant.
            const dmy = /^(\d{1,2})[\/-](\d{1,2})[\/-](\d{4})$/.exec(s);
            if (dmy) {
                const day = Number(dmy[1]);
                const month = Number(dmy[2]);
                const year = Number(dmy[3]);
                if (
                    Number.isFinite(day) &&
                    Number.isFinite(month) &&
                    Number.isFinite(year) &&
                    month >= 1 &&
                    month <= 12 &&
                    day >= 1 &&
                    day <= 31
                ) {
                    const dt = new Date(year, month - 1, day, 12, 0, 0, 0);
                    if (!isNaN(dt.getTime())) return toYmd(dt);
                }
            }
        }

        const d = val instanceof Date ? val : new Date(val);
        return isNaN(d.getTime()) ? "" : toYmd(d);
    };

    const parseTimeToHhmm = (val: any): string => {
        if (!val) return "";
        if (val instanceof Date && !isNaN(val.getTime())) {
            return `${String(val.getHours()).padStart(2, "0")}:${String(val.getMinutes()).padStart(2, "0")}`;
        }

        if (typeof val === "string") {
            const m = /^(\d{1,2}):(\d{2})$/.exec(val.trim());
            if (m) {
                return `${String(Number(m[1])).padStart(2, "0")}:${m[2]}`;
            }
        }

        const d = new Date(val);
        if (isNaN(d.getTime())) return "";
        return `${String(d.getHours()).padStart(2, "0")}:${String(d.getMinutes()).padStart(2, "0")}`;
    };

    const toNumberOrUndefined = (n: any): number | undefined => {
        if (typeof n === "number" && Number.isFinite(n)) return n;
        if (typeof n === "string" && n.trim().length > 0) {
            const parsed = Number(n);
            return Number.isFinite(parsed) ? parsed : undefined;
        }
        return undefined;
    };

    const parseAddressValue = (raw: any) => {
        let input = raw;
        if (typeof raw === "string") {
            const trimmed = raw.trim();
            if (trimmed.startsWith("{") && trimmed.endsWith("}")) {
                try {
                    input = JSON.parse(trimmed);
                } catch {
                    input = raw;
                }
            }
        }

        if (typeof input === "string") {
            return {
                googleAddress: input,
                latitude: undefined as number | undefined,
                longitude: undefined as number | undefined,
            };
        }

        if (input && typeof input === "object") {
            const nestedAddress =
                input.address &&
                    typeof input.address === "object" &&
                    !Array.isArray(input.address)
                    ? input.address
                    : null;

            const googleAddress =
                typeof input.googleAddress === "string" &&
                    input.googleAddress.trim().length > 0
                    ? input.googleAddress
                    : typeof input.address === "string" &&
                        input.address.trim().length > 0
                        ? input.address
                        : typeof input.formattedAddress === "string" &&
                            input.formattedAddress.trim().length > 0
                            ? input.formattedAddress
                            : typeof nestedAddress?.googleAddress === "string" &&
                                nestedAddress.googleAddress.trim().length > 0
                                ? nestedAddress.googleAddress
                                : typeof nestedAddress?.address === "string" &&
                                    nestedAddress.address.trim().length > 0
                                    ? nestedAddress.address
                                    : typeof nestedAddress?.formattedAddress === "string" &&
                                        nestedAddress.formattedAddress.trim().length > 0
                                        ? nestedAddress.formattedAddress
                                        : "";

            return {
                googleAddress,
                latitude: toNumberOrUndefined(
                    input.latitude ??
                    input.lat ??
                    nestedAddress?.latitude ??
                    nestedAddress?.lat,
                ),
                longitude: toNumberOrUndefined(
                    input.longitude ??
                    input.lng ??
                    nestedAddress?.longitude ??
                    nestedAddress?.lng,
                ),
            };
        }

        return {
            googleAddress: "",
            latitude: undefined as number | undefined,
            longitude: undefined as number | undefined,
        };
    };

    const toBool = (v: any) => {
        if (typeof v === "boolean") return v;
        if (typeof v === "string") {
            const s = v.trim().toLowerCase();
            if (s === "true") return true;
            if (s === "false") return false;
        }
        return Boolean(v);
    };

    for (const key of Object.keys(schema)) {
        const field = schema[key];
        if (!isField(field)) continue;

        const name = field.id || key;
        const v = answerMap ? answerMap[name] : undefined;

        switch (field.type) {
            case "date":
                iv[name] = parseDateToYmd(v);
                break;
            case "time":
                iv[name] = parseTimeToHhmm(v);
                break;
            case "checkbox":
                iv[name] = toBool(v);
                break;
            case "number":
                iv[name] = v === "" || v == null ? "" : Number(v);
                break;
            case "address":
                {
                    const parsed = parseAddressValue(v);
                    iv[name] = parsed.googleAddress;
                    if (parsed.latitude != null) iv[`${name}__latitude`] = parsed.latitude;
                    if (parsed.longitude != null) iv[`${name}__longitude`] = parsed.longitude;
                }
                break;
            case "select": {
                const opts = field.options ?? [];
                const raw = v ?? "";

                const match =
                    opts.find((o: any) => o?.value === raw) ||
                    opts.find((o: any) => o?.label === raw);

                iv[name] = match ? match.value : raw;
                break;
            }
            default:
                iv[name] = v ?? "";
                break;
        }
    }

    return iv;
}

/* -------------------- vehicle helpers -------------------- */

export function asImageDataUrl(v?: string | null) {
    return v && v.trim()
        ? v.startsWith("data:")
            ? v
            : `data:image/jpeg;base64,${v.trim()}`
        : "";
}

export function extractVehicleFromDetail(detailResp: any, nodeId: string) {
    const root = detailResp?.data?.data ?? detailResp?.data ?? detailResp;

    const sections = root?.formSections;
    if (!Array.isArray(sections) || !sections.length) return null;

    const section = sections.find(
        (s: any) => String(s?.formId ?? "") === String(nodeId),
    );
    if (!section) return null;

    const answers = section?.answers;
    if (!Array.isArray(answers) || !answers.length) return null;

    const vehicleAnswer = answers.find(
        (a: any) =>
            String(a?.questionType ?? "").toLowerCase() === "vehicle" ||
            String(a?.question ?? "").toLowerCase() === "vehicle",
    );

    if (!vehicleAnswer) return null;

    const vehicleId =
        vehicleAnswer?.vehicle?.id ??
        vehicleAnswer?.answer ??
        vehicleAnswer?.vehicleId ??
        null;

    if (
        !vehicleId ||
        String(vehicleId) === "00000000-0000-0000-0000-000000000000"
    )
        return null;

    return {
        vehicleId: String(vehicleId),
        vehicle: vehicleAnswer?.vehicle ?? null,
    };
}


export function buildDefaultsFromSchema(schema: Record<string, any>) {
    return buildInitialValuesFromAnswerMap(schema, null);
}
